import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const SkeletonBox = ({ className = '' }) => (
  <div className={`rounded animate-pulse ${className}`} style={{ backgroundColor: '#1f1f1f' }} />
);

const fmtM = (n) => {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000)?.toFixed(2) + 'M';
  if (n >= 1_000) return (n / 1_000)?.toFixed(1) + 'K';
  return String(Math.round(n));
};

const getPctColor = (pct) => {
  if (pct >= 100) return '#22c55e';
  if (pct >= 70) return '#f97316';
  return '#ef4444';
};

// FIX 6: Sortable columns
const SORTABLE_COLS = ['diamonds_m1', 'objective_diamonds', 'realized', 'pct'];

const ManagerObjectivesTable = ({ managerObjectives, currentData, serversData, selectedServer, loading }) => {
  const { t } = useTranslation();
  // FIX 6: sort state
  const [sortKey, setSortKey] = useState('pct');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    if (!SORTABLE_COLS?.includes(key)) return;
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortArrow = ({ col }) => {
    if (!SORTABLE_COLS?.includes(col)) return null;
    if (sortKey !== col) return <span style={{ color: 'rgba(255,255,255,0.2)', marginLeft: '4px' }}>↕</span>;
    return <span style={{ color: '#E5202A', marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const rows = useMemo(() => {
    if (!managerObjectives?.length) return [];

    // FIX A1-B: Filter manager objectives by selectedServer client-side
    const filteredObjectives = selectedServer && selectedServer !== 'Tous'
      ? managerObjectives?.filter(o => o?.server === selectedServer)
      : managerObjectives;

    // Build realized per manager+server from currentData
    const managerRealized = {};
    currentData?.forEach((r) => {
      if (!r?.manager_id) return;
      if (!managerRealized?.[r?.manager_id]) managerRealized[r.manager_id] = {};
      if (!managerRealized?.[r?.manager_id]?.[r?.server]) managerRealized[r.manager_id][r.server] = 0;
      managerRealized[r.manager_id][r.server] += r?.diamonds || 0;
    });

    const mapped = filteredObjectives?.map((obj) => {
      const managerName = obj?.users?.name || `Manager ${obj?.manager_id?.slice(0, 8)}`;
      const realized = managerRealized?.[obj?.manager_id]?.[obj?.server] || 0;
      const objective = obj?.objective_diamonds || 0;
      const pct = objective > 0 ? (realized / objective) * 100 : 0;
      const srvData = serversData?.find((s) => s?.id === obj?.server);
      const color = srvData?.color || '#E5202A';
      return {
        manager_id: obj?.manager_id,
        managerName,
        server: obj?.server,
        serverColor: color,
        diamonds_m1: obj?.diamonds_m1 || 0,
        objective_diamonds: objective,
        realized,
        pct,
      };
    });

    // FIX 6: apply sort
    return [...mapped]?.sort((a, b) => {
      const aVal = a?.[sortKey] ?? 0;
      const bVal = b?.[sortKey] ?? 0;
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [managerObjectives, currentData, serversData, selectedServer, sortKey, sortDir]);

  const COLUMNS = [
    { key: 'managerName', label: 'Manager' },
    { key: 'server', label: t('admin.performanceActuelle.colServer', 'Server') },
    { key: 'diamonds_m1', label: 'Diamonds M-1' },
    { key: 'objective_diamonds', label: t('admin.performanceActuelle.colObjective', 'Objective') },
    { key: 'realized', label: t('admin.performanceActuelle.realized') },
    { key: 'pct', label: '%' },
    { key: 'progression', label: t('admin.performanceActuelle.colProgression', 'Progression') },
  ];

  if (loading) {
    return (
      <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
        <div className="p-4 space-y-3">
          {[1, 2, 3, 4]?.map((i) => (
            <SkeletonBox key={i} className="h-10 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!rows?.length) {
    return (
      <div
        className="rounded-xl p-5 border flex items-center justify-center h-24"
        style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}
      >
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
          {t('admin.performanceActuelle.noManagerObjective')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
      <div className="overflow-x-auto">
        <table className="w-full" style={{ minWidth: '800px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
              {COLUMNS?.map((col) => (
                <th
                  key={col?.key}
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${SORTABLE_COLS?.includes(col?.key) ? 'cursor-pointer select-none' : ''}`}
                  style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}
                  onClick={() => handleSort(col?.key)}
                >
                  {col?.label}<SortArrow col={col?.key} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows?.map((row, idx) => {
              const pctColor = getPctColor(row?.pct);
              return (
                <tr
                  key={`${row?.manager_id}-${row?.server}-${idx}`}
                  style={{ borderBottom: '1px solid #1a1a1a' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {row?.managerName}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-md"
                      style={{
                        backgroundColor: `${row?.serverColor}22`,
                        color: row?.serverColor,
                        fontFamily: 'Sora, sans-serif',
                      }}
                    >
                      {row?.server}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif' }}>
                      💎 {fmtM(row?.diamonds_m1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Inter, sans-serif' }}>
                      💎 {fmtM(row?.objective_diamonds)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold" style={{ color: row?.serverColor, fontFamily: 'Inter, sans-serif' }}>
                      💎 {fmtM(row?.realized)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-bold" style={{ color: pctColor, fontFamily: 'Sora, sans-serif' }}>
                      {row?.pct?.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-4 py-3" style={{ minWidth: '120px' }}>
                    <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#1f1f1f' }}>
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(row?.pct, 100)}%`, backgroundColor: pctColor }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManagerObjectivesTable;
